import { computed, inject, Injectable, signal } from '@angular/core';
import type { Signal, WritableSignal } from '@angular/core';
import { environment } from 'src/environments/environment';

import { AnalyticsService } from './analytics.service';

export enum CookieCategory {
  Necessary = 'C0001',
  Performance = 'C0002',
  Functional = 'C0003',
  Marketing = 'C0004',
}

const COOKIE_CONSENT_SCRIPT_ID = 'cookie-consent-script';
const COOKIE_CONSENT_SCRIPT_SRC = environment.cookieConsent.scriptSrc;

const COOKIE_CONSENT_DOMAIN_SCRIPT_ID =
  environment.cookieConsent.domainScriptId;

declare global {
  interface Window {
    Optanon: {
      changeLanguage?: () => void;
    };
    OptanonActiveGroups: string;
    OptanonWrapper: () => void;
  }
}

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  public cookieScriptLoaded: WritableSignal<boolean> = signal(false);

  private acceptedCategories: WritableSignal<string[]> = signal([]);

  public analyticsAllowed: Signal<boolean> = computed(() =>
    this.acceptedCategories().includes(CookieCategory.Performance)
  );

  private analytics = inject(AnalyticsService);

  initializeCookieConsent(): void {
    if (this.isCookieScriptPresent()) {
      return;
    }

    window.OptanonWrapper = () => this.handleConsent();

    this.loadScript();
  }

  setLanguage(language: string): void {
    document.documentElement.setAttribute('lang', language);

    if (!this.cookieScriptLoaded()) {
      return;
    }

    if (window.Optanon?.changeLanguage) {
      window.Optanon.changeLanguage();
    }
  }

  private isCookieScriptPresent(): boolean {
    return Boolean(
      this.cookieScriptLoaded() ||
        document.getElementById(COOKIE_CONSENT_SCRIPT_ID)
    );
  }

  private handleConsent(): void {
    if (!this.cookieScriptLoaded()) {
      return;
    }

    this.setAcceptedCategories();

    if (this.analyticsAllowed()) {
      this.analytics.addAdobeLaunchScript();
    } else {
      this.analytics.removeAdobeLaunchScript();
    }
  }

  private loadScript(): void {
    const script = document.createElement('script');

    script.id = COOKIE_CONSENT_SCRIPT_ID;
    script.src = COOKIE_CONSENT_SCRIPT_SRC;
    script.type = 'text/javascript';
    script.async = true;

    script.setAttribute('data-domain-script', COOKIE_CONSENT_DOMAIN_SCRIPT_ID);
    script.setAttribute('data-document-language', 'true');

    script.onload = () => {
      this.cookieScriptLoaded.set(true);
      this.setAcceptedCategories();
    };

    script.onerror = () => {
      console.error('Error loading cookie consent script');
    };

    document.head.appendChild(script);
  }

  private setAcceptedCategories(): void {
    const acceptedCategories =
      window.OptanonActiveGroups?.split(',')
        .map((c) => c.trim())
        .filter((c) => c !== '') ?? [];

    this.acceptedCategories.set(acceptedCategories);
  }
}
